import { useQueryClient } from '@tanstack/react-query';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CurrencyFilterSelector } from 'src/components/Selectors/CurrencyFilterSelector';
import { Content, useContent } from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { MarketplacePaymentLineWithSaleAndEventInfo } from 'src/hooks/useGetMarketplacePaymentLineTableData';
import { ContentId } from 'src/utils/constants/contentId';
import { MARKETPLACE_PAYMENT_LINE_V2_TYPE_TO_CID } from 'src/utils/constants/contentIdMaps';
import { posChangedField, posField } from 'src/utils/posFieldUtils';
import {
  MarketplacePaymentLineType,
  MarketplacePaymentSaleLineInput,
  Sale,
} from 'src/WebApiController';

import { SaleEventBox } from '../LinkMarketplacePaymentLineDialog/SaleEventBox/SaleEventBox';

export interface EditMarketplacePaymentLineRefActions {
  showDialog: () => void;
}

export type EditMarketplacePaymentLineDialogProps = {
  refActions: MutableRefObject<EditMarketplacePaymentLineRefActions | null>;
  paymentLineData?: MarketplacePaymentLineWithSaleAndEventInfo;
  marketplacePaymentId: number;
  onSubmit: (data: MarketplacePaymentSaleLineInput) => Promise<void>;
};

const getNegatedAmountForPaymentLineType = (
  amount: number,
  paymentLineType: MarketplacePaymentLineType
): number => {
  const isChargeType = paymentLineType === MarketplacePaymentLineType.Charge;
  if (isChargeType && amount > 0) {
    return amount * -1;
  } else if (!isChargeType && amount < 0) {
    return amount * -1;
  }
  return amount;
};

const getPaymentLineFormValues = (
  paymentLineData: MarketplacePaymentLineWithSaleAndEventInfo | undefined,
  marketplacePaymentId: number
): MarketplacePaymentSaleLineInput => {
  return {
    marketplacePaymentLineId: paymentLineData?.id ?? 0,
    marketplacePaymentId,
    externalPaymentLineId: posField(paymentLineData?.extLineId ?? ''),
    paymentLineType: posField(
      paymentLineData?.type ?? MarketplacePaymentLineType.Proceeds
    ),
    saleId: posField(paymentLineData?.saleId ?? null),
    externalSaleId: posField(paymentLineData?.extSaleId ?? ''),
    amount: posField(paymentLineData?.amt.amt ?? 0),
    currency: posField(paymentLineData?.amt.currency ?? ''),
  };
};

export const EditMarketplacePaymentLineDialog = ({
  refActions,
  paymentLineData,
  marketplacePaymentId,
  onSubmit,
}: EditMarketplacePaymentLineDialogProps) => {
  const { launchDialog, closeDialog, dialogProps } = useBasicDialog();
  const requiredText = useContent(ContentId.Required);
  const linkSalePaymentReceivedText = useContent(
    ContentId.LinkPaymentReceivedError
  );
  const [isLoading, setIsLoading] = useState(false);
  const [saleIdExists, setSaleIdExists] = useState<boolean>(false);
  const [isSalePaymentReceived, setIsSalePaymentReceived] =
    useState<boolean>(false);
  const { getUiCurrency } = useLocalizationContext();
  const {
    register,
    setValue,
    control,
    watch,
    formState,
    getValues,
    reset,
    handleSubmit,
  } = useForm<MarketplacePaymentSaleLineInput>({
    defaultValues: getPaymentLineFormValues(
      paymentLineData,
      marketplacePaymentId
    ),
  });

  useEffect(() => {
    // Ensure marketplacePaymentId is always set
    setValue('marketplacePaymentId', marketplacePaymentId);
  }, [marketplacePaymentId, setValue]);

  const externalSaleId = watch('externalSaleId');

  const valueOptionsContent = useMemo(() => {
    return Object.entries(MARKETPLACE_PAYMENT_LINE_V2_TYPE_TO_CID).reduce(
      (acc, [key, value]) => {
        acc[key] = value;

        return acc;
      },
      {} as Record<string, string>
    );
  }, []);

  const currencyCode = watch('currency')?.value;
  const paymentLineAmount = watch('amount')?.value;
  const paymentLineType = watch('paymentLineType');

  const hasErrors = Object.keys(formState.errors).length > 0;
  const queryClient = useQueryClient();

  const isAlreadyPaymentReceived =
    isSalePaymentReceived &&
    paymentLineType?.hasChanged &&
    paymentLineData?.type !== MarketplacePaymentLineType.Proceeds &&
    paymentLineType.value === MarketplacePaymentLineType.Proceeds;

  const onSearchSaleChanged = useCallback(
    (saleDetails: Sale | undefined | null) => {
      setSaleIdExists(!!saleDetails);
      if (saleDetails?.id) {
        setValue('saleId', posChangedField(saleDetails.id));
      }
      setIsSalePaymentReceived(saleDetails?.pmtRcv || false);
    },
    []
  );

  const handleFormSubmit = useCallback(async () => {
    const input = getValues();
    input.marketplacePaymentId = marketplacePaymentId;
    setIsLoading(true);
    try {
      await onSubmit(input);
      closeDialog();
    } catch (error) {
      // Error is handled in the parent component
    } finally {
      setIsLoading(false);
    }
  }, [closeDialog, getValues, marketplacePaymentId, onSubmit]);

  useImperativeHandle(refActions, () => {
    return {
      showDialog: launchDialog,
    };
  }, [launchDialog]);

  if (!dialogProps.isOpen) {
    return null;
  }

  return (
    <GenericDialog
      header={
        <Content
          id={
            paymentLineData == null
              ? ContentId.AddMarketplacePaymentLine
              : ContentId.EditPaymentLine
          }
        />
      }
      footer={
        <>
          <Button
            variant="outline"
            onClick={() => {
              closeDialog();
            }}
          >
            <Content id={ContentId.Cancel} />
          </Button>
          {isLoading ? (
            <div>
              <PosSpinner size={vars.iconSize.xxs} />
            </div>
          ) : (
            <Button
              disabled={
                isLoading ||
                hasErrors ||
                (externalSaleId?.hasChanged && !saleIdExists) ||
                isAlreadyPaymentReceived
              }
              onClick={() => {
                handleSubmit(handleFormSubmit)();
              }}
            >
              <Content id={ContentId.Save} />
            </Button>
          )}
        </>
      }
      headerText={
        <Content
          id={
            paymentLineData == null
              ? ContentId.AddMarketplacePaymentLine
              : ContentId.EditPaymentLine
          }
        />
      }
      {...dialogProps}
      isLoading={isLoading}
      size="xl"
      onOpened={() => {
        reset(getPaymentLineFormValues(paymentLineData, marketplacePaymentId));
      }}
      onClosed={() => {
        reset();
      }}
    >
      <Stack direction="column" gap="xl">
        <PosFormField label={<Content id={ContentId.PaymentLineId} />}>
          <PosTextField
            {...register('externalPaymentLineId.value', {
              required: { value: true, message: requiredText },
              onChange: ({ target: { value } }) => {
                setValue('externalPaymentLineId', posChangedField(value));
              },
            })}
          />
        </PosFormField>

        <PosFormField
          errors={
            isAlreadyPaymentReceived
              ? linkSalePaymentReceivedText
              : formState.errors.paymentLineType?.message
          }
          label={<Content id={ContentId.PaymentType} />}
        >
          <Controller
            name={'paymentLineType.value'}
            rules={{ required: requiredText }} //
            control={control}
            render={({ field: { value } }) => (
              <PosSelect
                showFullWidth={true}
                value={value || undefined}
                valueOptionsContent={valueOptionsContent}
                searchable
                onChange={(paymentLineType) => {
                  if (paymentLineAmount) {
                    const negatedAmount = getNegatedAmountForPaymentLineType(
                      paymentLineAmount,
                      paymentLineType as MarketplacePaymentLineType
                    );
                    setValue('amount', posChangedField(negatedAmount));
                  }
                  setValue(
                    'paymentLineType',
                    posChangedField(
                      paymentLineType as MarketplacePaymentLineType
                    ),
                    { shouldValidate: true }
                  );
                }}
                sortBy="key"
              />
            )}
          />
        </PosFormField>
        <PosFormField label={<Content id={ContentId.ExternalSaleId} />}>
          <PosTextField
            {...register('externalSaleId.value', {
              onChange: ({ target: { value } }) => {
                setValue('externalSaleId', posChangedField(value));
              },

              required: { value: true, message: requiredText },
            })}
          />
        </PosFormField>

        <SaleEventBox
          lookupId={externalSaleId?.value}
          onSearchSaleChanged={onSearchSaleChanged}
          hideMargin={true}
        />
        <PosFormField label={<Content id={ContentId.PaymentAmount} />}>
          <PosCurrencyField
            uiCurrency={getUiCurrency(getValues(`currency.value`))}
            value={paymentLineAmount || undefined}
            {...register(`amount.value`, {
              valueAsNumber: true,
              onChange: ({ target: { value } }) => {
                if (paymentLineType?.value) {
                  const negatedAmount = getNegatedAmountForPaymentLineType(
                    value,
                    paymentLineType.value
                  );
                  setValue('amount', posChangedField(negatedAmount));
                } else {
                  setValue('amount', posChangedField(value));
                }
              },
              required: { value: true, message: requiredText },
            })}
          />
        </PosFormField>

        <PosFormField label={<Content id={ContentId.PaymentCurrency} />}>
          <Controller
            control={control}
            name="currency.value"
            rules={{ required: { value: true, message: requiredText } }}
            render={({ field: { onChange, value }, fieldState }) => (
              <CurrencyFilterSelector
                showFullWidth={true}
                value={value || ''}
                defaultValue={currencyCode || ''}
                onChange={(newCurrencyCode) => {
                  if (newCurrencyCode && newCurrencyCode !== value) {
                    setValue('currency', posChangedField(newCurrencyCode));
                    onChange(newCurrencyCode);
                  }
                }}
              />
            )}
          />
        </PosFormField>
      </Stack>
    </GenericDialog>
  );
};
